import React from "react";
import CrIcon from "./CrIcon";
import FancySelect from "./FancySelect";
import ToolIcon from "./ToolIcon";
import CustomAutomationRequest from "./CustomAutomationRequest";
import { showControlRoomTab, useRefreshOnReturn } from "../lib/controlRoomTabs";
import { dialog } from "../lib/dialog";

// Custom automations (backend server/customAutomations/): someone describes a
// workflow in their own words. AI reads it into a trigger and a checklist, BossDrop's
// decision layer runs each firing, and AI checks each result against what was asked.
//
// Its own routes rather than the workspace doc (/api/custom-automations), because the
// server stamps when one was turned on: only what happens after that fires it. The
// server is the authority on every field here.
//
// House rule: no em dashes in any copy on this section.

const MIN_PROMPT = 10;
const MAX_PROMPT = 4000;

const TOOL_ORDER = [
  "pipedrive",
  "hubspot",
  "monday",
  "asana",
  "jira",
  "linear",
  "clickup",
  "notion",
  "planner",
  "google",
  "outlook",
  "slack",
  "teams",
];
const TOOL_LABELS = {
  pipedrive: "Pipedrive",
  hubspot: "HubSpot",
  monday: "monday.com",
  asana: "Asana",
  jira: "Jira",
  linear: "Linear",
  clickup: "ClickUp",
  notion: "Notion",
  planner: "Planner",
  google: "Google Calendar",
  outlook: "Outlook",
  slack: "Slack",
  teams: "Microsoft Teams",
};
const TOOL_KINDS = [
  { label: "CRM", keys: ["pipedrive", "hubspot"] },
  { label: "Tasks", keys: ["monday", "asana", "jira", "linear", "clickup", "notion", "planner"] },
  { label: "Calendar", keys: ["google", "outlook"] },
  { label: "Chat", keys: ["slack", "teams"] },
];

const TRIGGERS = [
  { value: "deal_won", label: "A deal is won" },
  { value: "deal_stage", label: "A deal reaches a stage" },
  { value: "deal_lost", label: "A deal is lost" },
  { value: "deal_created", label: "A new deal is added" },
  { value: "schedule", label: "On a schedule" },
  { value: "manual", label: "Only when someone runs it" },
];
const DEAL_TRIGGERS = ["deal_won", "deal_stage", "deal_lost", "deal_created"];
const WEEK_DAYS = ["MON", "TUE", "WED", "THU", "FRI", "SAT", "SUN"];

const RUN_STATUS = {
  done: { tone: "ok", label: "Done" },
  running: { tone: "muted", label: "In progress" },
  awaiting_approval: { tone: "warn", label: "Waiting for approval" },
  held: { tone: "warn", label: "Held for the pilot" },
  denied: { tone: "muted", label: "Denied" },
  stopped: { tone: "muted", label: "Stopped" },
  needs_attention: { tone: "bad", label: "Needs you" },
};

const STEP_STATUS = {
  done: { mark: "ok", label: "Done" },
  not_done: { mark: "warn", label: "Not done yet" },
  blocked: { mark: "bad", label: "Needs someone" },
  not_needed: { mark: "muted", label: "Did not apply" },
  waiting: { mark: "muted", label: "Due later" },
  unknown: { mark: "muted", label: "Not checked" },
};

const ERRORS = {
  automation_prompt_too_short: "Tell BossDrop a little more about what you want done.",
  automation_prompt_too_long: "That is longer than BossDrop can read. Trim it to 4,000 characters.",
  automation_limit_reached: "You have reached the limit of 20 custom automations. Delete one first.",
  automation_build_failed: "BossDrop could not read that just now. Try again in a minute.",
  automation_not_found: "That automation no longer exists.",
  automation_not_built: "This automation has nothing built yet.",
  automation_has_questions: "Answer BossDrop's questions first, by adding the answers to your prompt.",
  automation_deal_required: "Choose a deal first.",
  automation_deal_not_found: "BossDrop cannot see that deal.",
  automation_busy: "This one is running right now.",
  automation_plan_unavailable: "BossDrop could not plan this just now. Try again in a minute.",
  automation_crm_unreadable: "BossDrop could not read your CRM just now, so it cannot say. Try again in a minute.",
  automation_test_slow: "That is taking longer than it should. Try again in a minute.",
  lock_not_acquired: "The workspace is busy. Try again in a minute.",
  pipedrive_not_connected: "Connect Pipedrive first.",
  hubspot_not_connected: "Connect HubSpot first.",
  pipedrive_reconnect_required: "Pipedrive sign-in expired. Reconnect it in Connectors.",
  hubspot_reconnect_required: "HubSpot sign-in expired. Reconnect it in Connectors.",
  pipedrive_rate_limited: "Pipedrive is limiting requests right now. Try again in a minute.",
  hubspot_rate_limited: "HubSpot is limiting requests right now. Try again in a minute.",
  custom_automation_rate_limited: "That was a lot of requests. Try again in a minute.",
};
const errorText = (code, fallback) => ERRORS[code] || fallback;

const POLL_MS = 4000;
// How long to keep reading after the server said it is working in the background.
const POLL_AFTER_ACCEPTED_MS = 3 * 60 * 1000;
const TEST_WAIT_MS = 4 * 60 * 1000;
// Coming back to the window fires focus and visibilitychange together. The section's own
// read spends none of a tool's API calls, so this only makes the two count as one.
const RETURN_READ_GAP_MS = 2000;

// A failed request is an answer here, never a thrown error: a card waiting on one must
// not stay frozen.
async function getJson(url, init) {
  try {
    const r = await fetch(url, { credentials: "include", cache: "no-store", ...init });
    const json = await r.json().catch(() => null);
    return { ok: r.ok, status: r.status, json };
  } catch {
    return { ok: false, status: 0, json: null };
  }
}

const send = (url, method, body) =>
  getJson(url, {
    method,
    headers: { "Content-Type": "application/json" },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });

function formatWhen(iso, tz) {
  const ms = Date.parse(iso || "");
  if (!Number.isFinite(ms)) return "";
  try {
    return new Intl.DateTimeFormat(undefined, {
      weekday: "short",
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
      ...(tz ? { timeZone: tz, timeZoneName: "short" } : {}),
    }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString();
  }
}

function formatDay(ymd) {
  const ms = Date.parse(`${String(ymd || "").slice(0, 10)}T12:00:00`);
  if (!Number.isFinite(ms)) return "";
  return new Intl.DateTimeFormat(undefined, { weekday: "short", month: "short", day: "numeric" }).format(new Date(ms));
}

/**
 * Finish connecting: opens that tool on the Connectors card, on the Context tab
 * (ConnectorsSection listens). A calendar is connected per person, in the Team
 * directory, so that is where it goes instead.
 */
export function openConnector(key) {
  showControlRoomTab("context");
  if (key === "google" || key === "outlook" || key === "any_calendar") {
    document.getElementById("cr-team-directory")?.scrollIntoView?.({ behavior: "smooth", block: "start" });
    return;
  }
  window.dispatchEvent(new CustomEvent("bd:open-connector", { detail: { key } }));
}

// FancySelect fills its container, so the width goes on a wrapper.
function Select({ width, ...props }) {
  return (
    <div style={{ width, maxWidth: "100%" }}>
      <FancySelect {...props} />
    </div>
  );
}

function ToolCheckboxes({ chosen, connected, onChange, disabled }) {
  const picked = new Set(chosen || []);
  return (
    <div className="customAutomations__tools">
      {TOOL_KINDS.map((kind) => (
        <div key={kind.label} className="customAutomations__toolGroup">
          <span className="muted small">{kind.label}</span>
          <div className="customAutomations__toolRow">
            {kind.keys.map((key) => (
              <label key={key} className="customAutomations__tool" title={connected?.[key] ? "Connected" : "Not connected yet"}>
                <input
                  type="checkbox"
                  className="bd-check"
                  checked={picked.has(key)}
                  disabled={disabled}
                  onChange={(e) => {
                    const next = new Set(picked);
                    if (e.target.checked) next.add(key);
                    else next.delete(key);
                    onChange(TOOL_ORDER.filter((k) => next.has(k)));
                  }}
                />
                <ToolIcon tool={key} />
                <span>{TOOL_LABELS[key]}</span>
              </label>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

function Composer({ apiBase, connected, onCreated, onCancel }) {
  const [prompt, setPrompt] = React.useState("");
  const [tools, setTools] = React.useState([]);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState("");
  const length = prompt.trim().length;
  const canCreate = !saving && length >= MIN_PROMPT && length <= MAX_PROMPT;

  async function create() {
    if (!canCreate) return;
    setSaving(true);
    setError("");
    const { ok, json } = await send(`${apiBase}/api/custom-automations`, "POST", {
      prompt: prompt.trim(),
      tools,
    });
    setSaving(false);
    if (!ok || !json?.ok) {
      setError(errorText(json?.error, "It did not save. Try again in a minute."));
      return;
    }
    onCreated(json.automation?.id || null);
  }

  return (
    <div className="ruleCard customAutomations__composer">
      <label className="crField">
        <span className="crFieldLabel">What should BossDrop do?</span>
        <textarea
          className="textareaFancy"
          rows={6}
          maxLength={MAX_PROMPT}
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          placeholder="For example: when a deal is won in Pipedrive, create a project in monday.com with our onboarding tasks, and book our three kickoff meetings with the client using Google Calendar."
          autoFocus
        />
        <span className="muted small">
          Say what starts it, what should happen, and who is involved. BossDrop reads this and shows you what it will
          do before anything runs.
        </span>
      </label>
      <div className="crField">
        <span className="crFieldLabel">Tools to use (optional)</span>
        <ToolCheckboxes chosen={tools} connected={connected} onChange={setTools} disabled={saving} />
        <span className="muted small">Leave these empty if your words already say which tools to use.</span>
      </div>
      {error ? (
        <div className="customAutomation__error" role="alert">
          {error}
        </div>
      ) : null}
      <div className="customAutomation__actions">
        <button type="button" className="btn" onClick={onCancel} disabled={saving}>
          Cancel
        </button>
        <button type="button" className="btn primary" onClick={create} disabled={!canCreate}>
          {saving ? "Creating..." : "Create automation"}
        </button>
      </div>
    </div>
  );
}

function ConnectCallout({ readiness, connections, canManage }) {
  const missing = (connections || []).filter((c) => !c.connected);
  // Questions have their own box on the card.
  const items = (readiness?.items || []).filter((item) => !item.ok && item.key !== "mode" && item.key !== "questions");
  if (!missing.length && !items.length) return null;
  const labelled = new Set(missing.map((c) => c.key));
  const others = items.filter((item) => !item.connect || !labelled.has(item.connect));
  return (
    <div className="customAutomations__callout" role="status">
      <div className="crFieldLabel">Finish setting this up</div>
      {missing.length ? (
        <div className="customAutomations__connects">
          {missing.map((c) => (
            <button
              key={c.key}
              type="button"
              className="btn"
              onClick={() => openConnector(c.key)}
              disabled={!canManage}
            >
              <ToolIcon tool={c.key} /> Connect {c.label}
            </button>
          ))}
        </div>
      ) : null}
      {others.length ? (
        <ul className="customAutomations__list">
          {others.map((item) => (
            <li key={`${item.key}:${item.text}`}>
              {item.text}
              {item.connect && item.connect !== "any_task" ? (
                <>
                  {" "}
                  <button type="button" className="customAutomations__link" onClick={() => openConnector(item.connect)}>
                    Go there
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

function RunRow({ run, canManage, onRunAgain, busy }) {
  const working = run.inFlight === true;
  // Still looking on its own, but a person can unstick it: that is what it says.
  const needsYou =
    run.status === "needs_attention" || (run.status === "running" && (run.needsPerson?.length > 0 || run.stuck === true));
  const [open, setOpen] = React.useState(needsYou);
  const status = working
    ? { tone: "muted", label: "Running now" }
    : needsYou
      ? RUN_STATUS.needs_attention
      : RUN_STATUS[run.status] || { tone: "muted", label: run.status };
  const when = formatWhen(run.startedAt);
  return (
    <div className="customAutomations__run">
      <button
        type="button"
        className="customAutomations__runHead"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
      >
        <span className="customAutomations__runTitle">{run.title || when || "Run"}</span>
        <span className="muted small">{run.title ? when : ""}</span>
        <span className={`chip ${status.tone}`}>{status.label}</span>
      </button>
      {open ? (
        <div className="customAutomations__runBody">
          {run.summary ? <div className="small">{run.summary}</div> : null}
          {run.needsPerson?.length ? (
            <>
              <div className="crFieldLabel">What it needs</div>
              <ul className="customAutomations__list">
                {run.needsPerson.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </>
          ) : null}
          {run.done?.length ? (
            <>
              <div className="crFieldLabel">What happened</div>
              <ul className="customAutomations__list">
                {run.done.map((d) => (
                  <li key={d.id}>
                    {d.url ? (
                      <a href={d.url} target="_blank" rel="noreferrer">
                        {d.title}
                      </a>
                    ) : (
                      d.title
                    )}
                    {d.startIso ? <span className="muted"> ({formatWhen(d.startIso, d.tz)})</span> : null}
                    {d.to ? <span className="muted"> (to {d.to})</span> : null}
                    {d.readBack === "gone" ? <span className="muted"> (no longer there)</span> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {run.failed?.length ? (
            <>
              <div className="crFieldLabel">Did not go through</div>
              <ul className="customAutomations__list">
                {run.failed.map((f) => (
                  <li key={`${f.type}:${f.title}`}>
                    {f.title}
                    {f.whyText ? <span className="muted">: {f.whyText}</span> : null}
                  </li>
                ))}
              </ul>
            </>
          ) : null}
          {run.lastError === "check_unavailable" ? (
            <div className="muted small">BossDrop could not check this run, so it is not calling it finished.</div>
          ) : null}
          {run.status === "running" && run.nextAttemptAt ? (
            <div className="muted small">Next look: {formatWhen(run.nextAttemptAt)}</div>
          ) : null}
          {run.watchUntil && run.status === "running" ? (
            <div className="muted small">
              BossDrop looks again every hour until {formatWhen(run.watchUntil)} and picks up by itself once that is done.
              If you put it right by hand, or change your prompt, press Run again.
            </div>
          ) : null}
          {run.stuck && run.status === "running" ? (
            <div className="muted small">
              BossDrop has stopped trying this for now, and looks again when a later step falls due.
            </div>
          ) : null}
          {(run.status !== "done" && run.status !== "running") || needsYou ? (
            canManage ? (
              <div>
                <button type="button" className="btn" disabled={busy || working} onClick={() => onRunAgain(run.id)}>
                  Run again
                </button>
              </div>
            ) : null
          ) : null}
        </div>
      ) : null}
    </div>
  );
}

function RehearsalResult({ result, steps, nameFor }) {
  const stepText = (id) => (steps || []).find((s) => s.id === id)?.text || "";
  const line = (a) => {
    if (a.type === "create_project") return `Create the project "${a.title}"`;
    if (a.type === "create_task") {
      return `Task "${a.title}"${a.ownerEmail ? `, for ${nameFor(a.ownerEmail)}` : ""}${a.dueISO ? `, due ${formatDay(a.dueISO)}` : ""}${a.inProject ? ", on the project" : ""}`;
    }
    if (a.type === "schedule_meeting") {
      return `Meeting "${a.title}"${a.durationMin ? `, ${a.durationMin} minutes` : ""}${a.startISO ? `, around ${formatDay(a.startISO)}` : ""}, with ${a.attendees.map(nameFor).join(", ")}`;
    }
    if (a.type === "send_message") return `Message ${nameFor(a.toEmail)}: "${a.title}"`;
    if (a.type === "update_task") return `Update the task "${a.title}"`;
    if (a.type === "update_meeting") return `Change the meeting "${a.title}"`;
    return a.title;
  };
  return (
    <div className="customAutomations__rehearsal" data-testid="automation-rehearsal">
      <div className="crFieldLabel">If this ran now, BossDrop would</div>
      {result.actions?.length ? (
        <ul className="customAutomations__list">
          {result.actions.map((a, i) => (
            <li key={`${a.type}:${i}`}>
              {line(a)}
              {(a.type === "send_message" || a.type === "schedule_meeting") && a.body ? (
                <div className="customAutomations__rehearsalText">{a.body}</div>
              ) : null}
            </li>
          ))}
        </ul>
      ) : (
        <div className="muted small">Do nothing for this one.</div>
      )}
      {result.notes?.length ? (
        <>
          <div className="crFieldLabel">And it would leave</div>
          <ul className="customAutomations__list">
            {result.notes.map((n, i) => (
              <li key={`${n.stepId}:${i}`}>
                {stepText(n.stepId) ? `${stepText(n.stepId)}: ` : ""}
                {n.why}
              </li>
            ))}
          </ul>
        </>
      ) : null}
      <div className="muted small">Nothing was created, booked or sent.</div>
    </div>
  );
}

function TriggerEditor({ apiBase, automation, connected, disabled, onSave, onScreen }) {
  const trigger = automation.definition?.trigger || {};
  // Saved when the person leaves the field: each save on a half-typed time could fire.
  const [time, setTime] = React.useState(trigger.time || "09:00");
  React.useEffect(() => setTime(trigger.time || "09:00"), [trigger.time]);
  const isDeal = DEAL_TRIGGERS.includes(trigger.type);
  const crm = trigger.crm || (connected?.pipedrive ? "pipedrive" : connected?.hubspot ? "hubspot" : "");
  const [pipelines, setPipelines] = React.useState([]);
  const wantsStages = trigger.type === "deal_stage" && !!crm && !!connected?.[crm];
  const stageReads = React.useRef(0);

  // Fresh, because a pipeline or stage may have just been made in the CRM's own tab.
  const readStages = React.useCallback(async () => {
    stageReads.current += 1;
    const read = stageReads.current;
    const { ok, json } = await getJson(`${apiBase}/api/${crm}/pipelines?fresh=1`);
    if (read === stageReads.current && ok) setPipelines(Array.isArray(json?.pipelines) ? json.pipelines : []);
  }, [apiBase, crm]);

  React.useEffect(() => {
    if (wantsStages && onScreen) readStages();
    return () => {
      stageReads.current += 1;
    };
  }, [wantsStages, onScreen, readStages]);
  useRefreshOnReturn(readStages, wantsStages && onScreen, { tab: null });

  const stages = pipelines.flatMap((p) =>
    (p.stages || []).map((s) => ({
      value: String(s.id),
      label: pipelines.length > 1 ? `${p.label}: ${s.label}` : s.label,
      stageLabel: s.label,
      stageOrder: s.displayOrder ?? null,
      pipelineId: String(p.id),
      pipelineLabel: p.label,
    })),
  );

  return (
    <div className="crField">
      <span className="crFieldLabel">When it runs</span>
      <div className="customAutomations__row">
        <Select
          value={trigger.type || "manual"}
          disabled={disabled}
          onChange={(v) => onSave({ trigger: { type: v } })}
          options={TRIGGERS}
          width={240}
          ariaLabel="When it runs"
        />
        {isDeal && connected?.pipedrive && connected?.hubspot ? (
          <Select
            value={trigger.crm || ""}
            disabled={disabled}
            onChange={(v) => onSave({ trigger: { crm: v, stageId: "", stageLabel: "", pipelineId: "", pipelineLabel: "" } })}
            options={[
              { value: "", label: "Either CRM" },
              { value: "pipedrive", label: "Pipedrive" },
              { value: "hubspot", label: "HubSpot" },
            ]}
            width={160}
            ariaLabel="Which CRM"
          />
        ) : null}
        {trigger.type === "deal_stage" ? (
          <Select
            value={trigger.stageId || ""}
            disabled={disabled || !stages.length}
            onChange={(v) => {
              const stage = stages.find((s) => s.value === v);
              if (!stage) return;
              onSave({
                trigger: {
                  stageId: stage.value,
                  stageLabel: stage.stageLabel,
                  stageOrder: stage.stageOrder,
                  pipelineId: stage.pipelineId,
                  pipelineLabel: stage.pipelineLabel,
                },
              });
            }}
            options={[
              { value: "", label: stages.length ? "Choose a stage" : trigger.stageLabel || "Connect the CRM to choose" },
              ...stages,
            ]}
            width={260}
            ariaLabel="Which stage"
          />
        ) : null}
        {trigger.type === "schedule" ? (
          <>
            <div className="customAutomations__days">
              {WEEK_DAYS.map((day) => (
                <label key={day} className="customAutomations__day">
                  <input
                    type="checkbox"
                    className="bd-check"
                    disabled={disabled}
                    checked={(trigger.days || []).includes(day)}
                    onChange={(e) => {
                      const next = new Set(trigger.days || []);
                      if (e.target.checked) next.add(day);
                      else next.delete(day);
                      onSave({ trigger: { days: WEEK_DAYS.filter((d) => next.has(d)) } });
                    }}
                  />
                  {day[0] + day.slice(1).toLowerCase()}
                </label>
              ))}
            </div>
            <input
              className="inputFancy"
              type="time"
              aria-label="At what time"
              value={time}
              disabled={disabled}
              onChange={(e) => setTime(e.target.value)}
              onBlur={() => {
                if (time && time !== trigger.time) onSave({ trigger: { time } });
              }}
            />
          </>
        ) : null}
      </div>
      {automation.when ? <span className="muted small">{automation.when}</span> : null}
    </div>
  );
}

// How far a build has got. The server says which step it is on (buildStep). Inside the
// long one, the model reading the words, the bar moves by the time gone and stops short
// of the end, because only the result says it has finished.
export function buildPercent(step, seconds) {
  if (step === "reading" || step === "retrying") {
    return Math.round(25 + 67 * (1 - Math.exp(-seconds / 30)));
  }
  return Math.min(25, 8 + seconds * 3);
}

function BuildProgress({ automation }) {
  const step = automation.buildStep || "tools";
  const since = Date.parse(automation.buildingSince || "");
  const [now, setNow] = React.useState(() => Date.now());
  React.useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(timer);
  }, []);
  const seconds = Number.isFinite(since) ? Math.max(0, Math.round((now - since) / 1000)) : 0;
  const percent = buildPercent(step, seconds);
  const toolsDone = step !== "tools";
  const clock = `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, "0")}`;
  const rows = [
    { key: "saved", state: "done", text: "Saved your words" },
    {
      key: "tools",
      state: toolsDone ? "done" : "doing",
      text: toolsDone ? "Looked at your connected tools" : "Looking at your connected tools",
    },
    {
      key: "reading",
      state: toolsDone ? "doing" : "todo",
      text:
        step === "retrying"
          ? "The first try did not come back, so BossDrop is trying again"
          : "Reading your prompt and working out the steps",
    },
  ];
  return (
    <div className="customAutomations__progress" data-testid="automation-progress">
      <div
        className="customAutomations__bar"
        role="progressbar"
        aria-label="Building this automation"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
      >
        <span style={{ width: `${percent}%` }} />
      </div>
      <ul className="customAutomations__progressSteps">
        {rows.map((row) => (
          <li key={row.key} className={`customAutomations__progressStep is-${row.state}`}>
            <span className="customAutomations__progressMark" aria-hidden="true" />
            <span className="small">{row.text}</span>
            {row.state === "doing" ? <span className="muted small">{clock}</span> : null}
          </li>
        ))}
      </ul>
      <div className="muted small" role="status">
        {seconds < 120
          ? "This can take a minute or two. You can leave this page: it keeps going, and this card shows what it found."
          : "This is taking longer than usual. It keeps going for up to five minutes, and this card says so if it cannot finish."}
      </div>
    </div>
  );
}

function AutomationCard({ apiBase, automation, connected, canManage, nameFor, onChanged, startOpen, onWorking, onScreen }) {
  const [open, setOpen] = React.useState(!!startOpen);
  const [editing, setEditing] = React.useState(false);
  const [prompt, setPrompt] = React.useState(automation.pendingPrompt || automation.prompt || "");
  const [tools, setTools] = React.useState(automation.integrations || []);
  const [busy, setBusy] = React.useState(false);
  const [message, setMessage] = React.useState("");
  const [deals, setDeals] = React.useState(null);
  const [deal, setDeal] = React.useState("");
  const [rehearsal, setRehearsal] = React.useState(null);
  const definition = automation.definition;
  const steps = definition?.steps || [];
  const built = steps.length > 0;
  const trigger = definition?.trigger;
  const isDeal = DEAL_TRIGGERS.includes(trigger?.type);
  const url = `${apiBase}/api/custom-automations/${encodeURIComponent(automation.id)}`;
  const dealsUrl = `${apiBase}/api/custom-automations/deals?automationId=${encodeURIComponent(automation.id)}`;
  const disabled = !canManage || busy;
  const lastRun = automation.runs?.[0] || null;
  const verdictSteps = new Map((lastRun?.steps || []).map((s) => [s.stepId, s]));
  const blockers = automation.blockers || [];
  const runInFlight = (automation.runs || []).some((r) => r.inFlight === true);
  const questions = automation.building ? [] : automation.notes?.questions || [];
  const cardRef = React.useRef(null);
  const editorRef = React.useRef(null);

  // A build's progress shows at the top of the card, and a new one may be further down
  // the list than the box it was written in.
  const showTop = () => cardRef.current?.scrollIntoView?.({ block: "start", behavior: "smooth" });
  React.useEffect(() => {
    if (startOpen) showTop();
  }, [startOpen]);

  // Ready to type at the end of what is there, which is where an answer goes.
  React.useEffect(() => {
    const box = editorRef.current;
    if (!editing || !box) return;
    box.focus();
    box.setSelectionRange(box.value.length, box.value.length);
    box.scrollTop = box.scrollHeight;
    box.scrollIntoView?.({ block: "nearest", behavior: "smooth" });
  }, [editing]);

  function startEditing() {
    setOpen(true);
    setEditing(true);
  }

  React.useEffect(() => {
    if (!editing) {
      setPrompt(automation.pendingPrompt || automation.prompt || "");
      setTools(automation.integrations || []);
    }
  }, [automation.prompt, automation.pendingPrompt, automation.integrations, editing]);

  async function save(edit) {
    setBusy(true);
    setMessage("");
    const { ok, status, json } = await send(url, "PUT", edit);
    setBusy(false);
    if (!ok || !json?.ok) {
      setMessage(errorText(json?.error, "Could not save. Try again."));
      return false;
    }
    // A new reading is on its way in the background.
    if (status === 202) onWorking();
    await onChanged();
    return true;
  }

  async function rebuild() {
    if (prompt.trim().length < MIN_PROMPT) {
      setMessage(errorText("automation_prompt_too_short"));
      return;
    }
    if (await save({ prompt: prompt.trim(), tools })) {
      setEditing(false);
      showTop();
    }
  }

  async function remove() {
    const confirmed = await dialog.confirm({
      title: `Delete "${automation.name || "this automation"}"?`,
      body: "Anything it already made stays where it is.",
      confirmLabel: "Delete",
      danger: true,
    });
    if (!confirmed) return;
    setBusy(true);
    const { ok } = await send(url, "DELETE");
    setBusy(false);
    if (!ok) {
      setMessage("Could not delete it. Try again.");
      return;
    }
    await onChanged();
  }

  async function loadDeals() {
    setDeals(null);
    const { ok, json } = await getJson(dealsUrl);
    if (!ok || !json?.ok) {
      // The button comes back, so the person can try again.
      setDeals(null);
      setMessage(errorText(json?.error, "Could not load recent deals."));
      return;
    }
    setDeals(json.deals || []);
  }

  // A list on screen keeps up with a deal just added in the CRM's own tab, or the CRM just
  // connected. A read that fails leaves it alone, and a deal that has gone stops being the
  // one chosen.
  async function rereadDeals() {
    const { ok, json } = await getJson(dealsUrl);
    if (!ok || !json?.ok) return;
    const list = json.deals || [];
    setDeals(list);
    setDeal((chosen) => (list.some((d) => `${d.crm}:${d.dealId}` === chosen) ? chosen : ""));
  }

  const dealsShown = deals !== null && open && onScreen;
  React.useEffect(() => {
    if (dealsShown) rereadDeals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, onScreen]);
  useRefreshOnReturn(rereadDeals, dealsShown, { tab: null });

  const dealTarget = () => {
    const [crm, dealId] = deal.split(":");
    return crm && dealId ? { crm, dealId } : {};
  };

  async function test() {
    setBusy(true);
    setMessage("");
    setRehearsal(null);
    const started = await send(`${url}/test`, "POST", dealTarget());
    let answer = started.json;
    // Reading the workspace and asking the model often outlast one request: the server
    // carries on, and the answer is read from where it keeps it.
    if (started.status === 202) {
      setMessage("BossDrop is working it out. This can take a minute or two.");
      answer = await waitForTest(Date.parse(started.json?.startedAt || "") || Date.now());
      setMessage("");
    } else if (!started.ok) {
      answer = started.json || null;
    }
    setBusy(false);
    if (!answer?.ok) {
      setMessage(errorText(answer?.error, "Could not test it. Try again."));
      return;
    }
    setRehearsal(answer);
  }

  async function waitForTest(startedAtMs) {
    const deadline = Date.now() + TEST_WAIT_MS;
    while (Date.now() < deadline) {
      await new Promise((resolve) => setTimeout(resolve, POLL_MS));
      const { ok, json } = await getJson(`${url}/test`);
      const result = ok ? json?.result : null;
      // Allow for the two clocks: what matters is that this answer is newer than the last.
      if (result && Date.parse(result.at || "") >= startedAtMs - 5000) return result;
    }
    return { ok: false, error: "automation_test_slow" };
  }

  async function run(body) {
    setBusy(true);
    setMessage("");
    const { ok, status, json } = await send(`${url}/run`, "POST", body);
    setBusy(false);
    if (status === 202) {
      setMessage("It is running. This list updates when it finishes.");
      onWorking();
    } else if (!ok || !json?.ok) {
      setMessage(errorText(json?.error, "Could not run it. Try again."));
    } else if (json.alreadyDone) {
      setMessage("This one already ran and finished, so BossDrop left it alone.");
    }
    await onChanged();
  }

  async function runNow() {
    const about = isDeal ? "this deal" : "now";
    const confirmed = await dialog.confirm({
      title: `Run "${automation.name}" for ${about}?`,
      body: "This does the real work.",
      confirmLabel: "Run now",
    });
    if (!confirmed) return;
    await run(dealTarget());
  }

  const statusLabel = automation.building
    ? "Building"
    : !built
      ? "Not built"
      : automation.enabled
        ? automation.readiness?.ready
          ? "On"
          : "On, needs setup"
        : blockers.length
          ? "Needs your answer"
          : "Off";
  const statusTone = automation.building
    ? "muted"
    : !built
      ? "bad"
      : automation.enabled
        ? automation.readiness?.ready
          ? "ok"
          : "warn"
        : blockers.length
          ? "warn"
          : "muted";

  return (
    <div ref={cardRef} className="ruleCard customAutomations__card" data-testid={`automation-${automation.id}`}>
      <div className="crCardHead">
        <div className="crCardHead__main">
          <div className="ruleTitle crCardHead__title">
            <CrIcon name="automation" />
            {automation.name || "New automation"}
          </div>
          {automation.building && open ? null : (
            <div className="muted small crCardHead__sub customAutomations__sub">
              {automation.building ? automation.pendingPrompt || automation.prompt : automation.when || automation.prompt}
            </div>
          )}
        </div>
        <div className="crCardHead__aside">
          <span className={`chip ${statusTone}`}>{statusLabel}</span>
          <input
            className="switchInput"
            type="checkbox"
            checked={!!automation.enabled}
            disabled={disabled || !built || automation.building || (!automation.enabled && blockers.length > 0)}
            title={!automation.enabled && blockers.length ? "Settle what is listed below first" : undefined}
            onChange={(e) => save({ enabled: e.target.checked })}
            aria-label={`Turn ${automation.name || "automation"} on or off`}
          />
        </div>
      </div>

      {automation.building ? <BuildProgress automation={automation} /> : null}

      {automation.buildError && !automation.building ? (
        <div className="customAutomation__error" role="alert">
          {automation.buildError === "automation_not_buildable"
            ? "BossDrop cannot do what this asks. See what it says below, or ask the BossDrop team."
            : errorText("automation_build_failed")}{" "}
          {canManage ? (
            <button
              type="button"
              className="customAutomations__link"
              onClick={() => save({ prompt: automation.pendingPrompt || automation.prompt })}
            >
              Try again
            </button>
          ) : null}
        </div>
      ) : null}

      {automation.offReason === "rebuilt" && !blockers.length ? (
        <div className="muted small" role="status">
          BossDrop read your new words, so this is off until you have read what it will do now and turned it back on.
        </div>
      ) : null}

      {questions.length ? (
        <div className="customAutomations__callout customAutomations__questions" role="status">
          <div className="crFieldLabel">BossDrop needs to know</div>
          <ul className="customAutomations__list">
            {questions.map((q) => (
              <li key={q}>{q}</li>
            ))}
          </ul>
          <span className="muted small">
            It stays off until these are answered, so nothing goes out on a guess. Add the answers to your prompt, and
            BossDrop reads it again.
          </span>
          {canManage && !editing ? (
            <div>
              <button type="button" className="btn primary" onClick={startEditing} disabled={busy}>
                Edit prompt
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {built ? (
        <ConnectCallout readiness={automation.readiness} connections={automation.readiness?.connections} canManage={canManage} />
      ) : null}

      <div className="customAutomations__cardActions">
        {canManage && !editing && !automation.building && !questions.length ? (
          <button type="button" className="btn" onClick={startEditing} disabled={busy}>
            Edit prompt
          </button>
        ) : null}
        <button type="button" className="customAutomations__link customAutomations__toggle" onClick={() => setOpen((v) => !v)} aria-expanded={open}>
          {open ? "Hide details" : "Details"}
        </button>
      </div>

      {open ? (
        <div className="ruleCardBody customAutomations__body">
          {editing ? (
            <>
              <label className="crField">
                <span className="crFieldLabel">Your prompt</span>
                <textarea
                  ref={editorRef}
                  className="textareaFancy"
                  rows={6}
                  maxLength={MAX_PROMPT}
                  value={prompt}
                  disabled={disabled}
                  onChange={(e) => setPrompt(e.target.value)}
                />
              </label>
              <div className="crField">
                <span className="crFieldLabel">Tools to use (optional)</span>
                <ToolCheckboxes chosen={tools} connected={connected} onChange={setTools} disabled={disabled} />
              </div>
              <div className="customAutomation__actions">
                <button type="button" className="btn" onClick={() => setEditing(false)} disabled={busy}>
                  Cancel
                </button>
                <button type="button" className="btn primary" onClick={rebuild} disabled={disabled}>
                  Update automation
                </button>
              </div>
            </>
          ) : (
            <div className="crField">
              <span className="crFieldLabel">Your prompt</span>
              <div className="small customAutomations__prompt">
                {automation.building ? automation.pendingPrompt || automation.prompt : automation.prompt}
              </div>
            </div>
          )}

          {built ? (
            <div className="crField">
              <span className="crFieldLabel">What BossDrop will do</span>
              <ol className="customAutomations__steps">
                {steps.map((step) => {
                  const said = verdictSteps.get(step.id);
                  const state = said ? STEP_STATUS[said.status] : null;
                  return (
                    <li key={step.id}>
                      {step.text}
                      {state ? <span className={`chip ${state.mark} customAutomations__stepChip`}>{state.label}</span> : null}
                    </li>
                  );
                })}
              </ol>
              <span className="muted small">
                Each time this runs, BossDrop decides the exact actions from your words and this list, then checks the
                result against them. {lastRun ? "The marks are from the last run." : ""}
              </span>
            </div>
          ) : null}

          {automation.notes?.assumptions?.length ? (
            <div className="crField">
              <span className="crFieldLabel">What it assumed</span>
              <ul className="customAutomations__list">
                {automation.notes.assumptions.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {automation.notes?.unsupported?.length ? (
            <div className="crField">
              <span className="crFieldLabel">What it cannot do</span>
              <ul className="customAutomations__list">
                {automation.notes.unsupported.map((line) => (
                  <li key={line}>{line}</li>
                ))}
              </ul>
              {canManage ? (
                <span className="muted small">
                  Need one of these? Use "Request a custom automation" at the bottom of this section, and the BossDrop team
                  will reply by email.
                </span>
              ) : null}
            </div>
          ) : null}

          {built ? (
            <TriggerEditor
              apiBase={apiBase}
              automation={automation}
              connected={connected}
              disabled={disabled}
              onSave={(edit) => save(edit)}
              onScreen={onScreen}
            />
          ) : null}

          {built && automation.readiness?.items?.length ? (
            <div className="crField">
              <span className="crFieldLabel">Ready to run</span>
              <ul className="customAutomations__ready">
                {automation.readiness.items.map((item) => (
                  <li key={`${item.key}:${item.text}`}>
                    <span className={`chip ${item.ok ? "ok" : item.key === "mode" ? "muted" : "warn"}`}>
                      {item.ok ? "OK" : item.key === "mode" ? "Note" : "To do"}
                    </span>
                    <span className="small">{item.text}</span>
                    {!item.ok && item.connect && item.connect !== "any_task" ? (
                      <button type="button" className="customAutomations__link" onClick={() => openConnector(item.connect)}>
                        Go there
                      </button>
                    ) : null}
                  </li>
                ))}
              </ul>
            </div>
          ) : null}

          {built ? (
            <label className="customAutomations__ask">
              <input
                type="checkbox"
                className="bd-check"
                checked={!!automation.askFirst}
                disabled={disabled}
                onChange={(e) => save({ askFirst: e.target.checked })}
              />
              <span className="small">Ask me before each run</span>
            </label>
          ) : null}

          {built && canManage ? (
            <div className="crField">
              <span className="crFieldLabel">Try it</span>
              <div className="customAutomations__row">
                {isDeal ? (
                  deals === null ? (
                    <button type="button" className="btn" disabled={busy} onClick={loadDeals}>
                      Choose a deal
                    </button>
                  ) : (
                    <Select
                      value={deal}
                      disabled={disabled || !deals.length}
                      onChange={setDeal}
                      options={[
                        { value: "", label: deals.length ? "Choose a deal" : "No recent deals" },
                        ...deals.map((d) => ({
                          value: `${d.crm}:${d.dealId}`,
                          label: `${d.title}${d.stage ? ` (${d.stage})` : ""}${d.runStatus ? `, ${RUN_STATUS[d.runStatus]?.label || d.runStatus}` : ""}`,
                        })),
                      ]}
                      width={320}
                      ariaLabel="Choose a deal"
                    />
                  )
                ) : null}
                <button type="button" className="btn" disabled={disabled || (isDeal && !deal)} onClick={test}>
                  {busy ? "Working..." : "Test it"}
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={disabled || runInFlight || (isDeal && !deal) || automation.notes?.questions?.length > 0}
                  onClick={runNow}
                >
                  Run now
                </button>
              </div>
              <span className="muted small">Test it shows what BossDrop would do, and changes nothing. Run now does the real work.</span>
            </div>
          ) : null}

          {rehearsal ? <RehearsalResult result={rehearsal} steps={steps} nameFor={nameFor} /> : null}

          {automation.runs?.length ? (
            <div className="crField">
              <span className="crFieldLabel">Recent runs</span>
              <div className="customAutomations__runs">
                {automation.runs.map((r) => (
                  <RunRow key={r.id} run={r} canManage={canManage} busy={busy} onRunAgain={(id) => run({ runId: id })} />
                ))}
              </div>
            </div>
          ) : null}

          {canManage ? (
            <div>
              <button type="button" className="customAutomations__link customAutomations__delete" onClick={remove} disabled={busy}>
                Delete this automation
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {message ? (
        <div className="muted small" role="status">
          {message}
        </div>
      ) : null}
    </div>
  );
}

export default function CustomAutomations({ apiBase, canManageWorkspace, teamMembers, onScreen = true, settingsSaves = 0 }) {
  const [data, setData] = React.useState(null);
  const [loadError, setLoadError] = React.useState("");
  const [composing, setComposing] = React.useState(false);
  const [openId, setOpenId] = React.useState(null);

  const nameFor = React.useCallback(
    (email) => {
      const e = String(email || "").toLowerCase();
      const person = (Array.isArray(teamMembers) ? teamMembers : []).find(
        (m) => String(m?.email || "").toLowerCase() === e,
      );
      return person?.name || email || "";
    },
    [teamMembers],
  );

  const [pollUntil, setPollUntil] = React.useState(0);
  const failures = React.useRef(0);
  const polls = React.useRef(0);
  const readsStarted = React.useRef(0);
  const newestAnswer = React.useRef(0);
  // `quiet` is a look again behind the scenes: if it fails, what is on screen stands.
  const refresh = React.useCallback(async ({ quiet = false } = {}) => {
    readsStarted.current += 1;
    const read = readsStarted.current;
    const { ok, json } = await getJson(`${apiBase}/api/custom-automations`);
    // Reads can cross on the way back, and an older answer must not undo a newer one.
    if (read < newestAnswer.current) return;
    newestAnswer.current = read;
    if (!ok || !json?.ok) {
      failures.current += 1;
      if (!quiet) setLoadError("Could not load your custom automations. Try again in a moment.");
      return;
    }
    failures.current = 0;
    setLoadError("");
    setData(json);
  }, [apiBase]);
  const startPolling = React.useCallback(() => setPollUntil(Date.now() + POLL_AFTER_ACCEPTED_MS), []);

  // This tab stays mounted, only hidden, so without these what the section first read would
  // stand until a reload: a tool connected on the Context tab, a default board chosen, someone
  // added to the team. So it reads again when the tab comes back, when someone returns to the
  // window, and when a settings save lands while it is on screen, because the server checks
  // a connection against the saved settings, which land just after the sign-in does.
  const firstRead = React.useRef(true);
  React.useEffect(() => {
    if (firstRead.current) {
      firstRead.current = false;
      refresh();
    } else if (onScreen) {
      refresh({ quiet: true });
    }
  }, [refresh, onScreen, settingsSaves]);
  useRefreshOnReturn(() => refresh({ quiet: true }), onScreen, { tab: null, gapMs: RETURN_READ_GAP_MS });

  // A build or a run happens on the server: keep reading until it is done.
  const working =
    pollUntil > 0 ||
    (data?.automations || []).some((a) => a.building || (a.runs || []).some((r) => r.inFlight === true));
  React.useEffect(() => {
    if (!working) return undefined;
    const timer = setInterval(() => {
      // Slowly against a server that is not answering, and it picks up when it does.
      polls.current += 1;
      if (failures.current >= 5 && polls.current % 15 !== 0) return;
      if (pollUntil > 0 && Date.now() > pollUntil) setPollUntil(0);
      refresh();
    }, POLL_MS);
    return () => clearInterval(timer);
  }, [working, refresh, pollUntil]);

  const automations = data?.automations || [];

  return (
    <div className="panelSub customAutomations" data-testid="custom-automations">
      <div className="crCardHead">
        <h3 className="crTitle">
          <CrIcon name="automation" size={18} />
          Custom automations
        </h3>
        {canManageWorkspace && !composing ? (
          <div className="crCardHead__aside">
            <button type="button" className="btn primary" onClick={() => setComposing(true)}>
              Create a custom automation
            </button>
          </div>
        ) : null}
      </div>
      <div className="muted small crCardBody">
        Describe a workflow in your own words. BossDrop works out when it should run and what it should do, runs it with
        your tools, and checks each result against what you asked for.
      </div>

      {loadError ? (
        <div className="customAutomation__error" role="alert">
          {loadError}
        </div>
      ) : null}

      {composing ? (
        <Composer
          apiBase={apiBase}
          connected={data?.connected}
          onCancel={() => setComposing(false)}
          onCreated={async (id) => {
            setComposing(false);
            if (id) setOpenId(id);
            await refresh();
          }}
        />
      ) : null}

      {automations.map((automation) => (
        <AutomationCard
          key={automation.id}
          apiBase={apiBase}
          automation={automation}
          connected={data?.connected}
          canManage={canManageWorkspace}
          nameFor={nameFor}
          onChanged={refresh}
          onWorking={startPolling}
          startOpen={openId === automation.id}
          onScreen={onScreen}
        />
      ))}

      {/* What BossDrop cannot build itself, in the customer's own words, emailed to
          the BossDrop team. */}
      {canManageWorkspace ? <CustomAutomationRequest /> : null}
    </div>
  );
}
